<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter, RouterLink } from 'vue-router';
import { Check, Sparkles, Lock } from '@lucide/vue';
import { Button } from '@/components/ui/button';
import { useHead } from '@unhead/vue';
import ScrollReveal from '@/components/ui/animation/ScrollReveal.vue';
import { SPREAD_SEO_DATA } from '@app/data/spreadSeoData';
import { CARD_SEO_DATA } from '@app/data/cardSeoData';
import {
  APP_CTA_TO,
  APP_CTA_LABEL,
  TELEGRAM_CTA_LABEL,
  TELEGRAM_BOT_URL,
} from '@app/presentation/components/site/constants';
import { spreadCutout, CARD_BACK_SRC } from '@app/data/spreadCutouts';
import CardFan from '@app/presentation/components/paper/CardFan.vue';
import PaperCutout from '@app/presentation/components/paper/PaperCutout.vue';
import SeoCrumbs from '@app/presentation/components/seo/SeoCrumbs.vue';
import SeoFaqItem from '@app/presentation/components/seo/SeoFaqItem.vue';
import SeoFinalCta from '@app/presentation/components/seo/SeoFinalCta.vue';
import SeoSpreadPatch from '@app/presentation/components/seo/SeoSpreadPatch.vue';
import SeoSpreadExample from '@app/presentation/components/seo/SeoSpreadExample.vue';
import SpreadQuickDraw from '@app/presentation/components/seo/SpreadQuickDraw.vue';

const route = useRoute();
const router = useRouter();

const slug = route.params.slug as string;
const spread = SPREAD_SEO_DATA[slug];

if (!spread) {
  router.replace('/rasklady');
}

// Hero art: her drawing as a cut-out (the spread's tile art in the app), else a fan of
// face-down cards — one per card in the reading.
const art = spread ? spreadCutout(spread.id) : undefined;
const fanBacks = Array.from({ length: Math.min(7, spread?.cardCount ?? 1) }, () => CARD_BACK_SRC);

// Static theme clusters → internal cross-links between thematically related
// spreads (SEO de-orphaning). Computed at setup scope so vite-ssg emits the
// <a href="/rasklady/<slug>"> links into the pre-rendered HTML.
const THEME_CLUSTERS: string[][] = [
  // Любовь и отношения
  ['lyubovnyj-rasklad', 'rasklad-na-byvshego', 'pochemu-on-propal', 'zhdat-ili-otpustit'],
  // Решения и выбор
  ['da-ili-net', 'a-ili-b', 'uvolnyatsya-nelzya-ostatsya', 'gadanie-na-situaciyu'],
  // Быстрые и бесплатные / вводные
  ['karta-dnya', 'zadaj-vopros', 'rasklad-na-tri-karty', 'da-ili-net'],
  // Карьера, деньги, рост
  ['finansovyj-rasklad', 'rasklad-na-uchebu', 'uvolnyatsya-nelzya-ostatsya', 'rasklad-na-talant'],
  // Путь и саморефлексия
  ['rasklad-sudby', 'rasklad-na-samopoznanie', 'rasklad-na-talant', 'gadanie-na-situaciyu'],
];

const DEFAULT_RELATED = ['karta-dnya', 'da-ili-net', 'lyubovnyj-rasklad', 'rasklad-na-tri-karty'];

const relatedSpreads = computed(() => {
  if (!spread) return [];
  const cluster = THEME_CLUSTERS.find(c => c.includes(slug)) ?? [];
  // current cluster first, then defaults as fallback to ensure we always show 3–4
  const ordered = [...cluster, ...DEFAULT_RELATED];
  const seen = new Set<string>([slug]);
  const result: typeof SPREAD_SEO_DATA[string][] = [];
  for (const s of ordered) {
    if (seen.has(s)) continue;
    const data = SPREAD_SEO_DATA[s];
    if (!data) continue;
    seen.add(s);
    result.push(data);
    if (result.length === 4) break;
  }
  return result;
});

// SEO pilot (docs/seo/roadmap-2026-10.md): only the 4 test pages carry `pilot` copy; the
// 12 control pages fall back to the original fields, so their HTML doesn't change. All of
// it is resolved at setup scope so vite-ssg bakes the text and <a href> links into the HTML.
const pilot = spread?.pilot;
const metaTitle = pilot?.metaTitle ?? spread?.metaTitle ?? '';
const metaDescription = pilot?.metaDescription ?? spread?.metaDescription ?? '';
const heading = pilot?.h1 ?? spread?.title ?? '';
const faqItems = pilot?.faq ?? spread?.content.faq ?? [];

const pilotIntro = pilot ? pilot.intro.split(/\n\s*\n/) : [];

// A bullet that opens with a short term ("Кубки — …", "«Его чувства» — …") gets the term
// set in strong ink; the text itself is unchanged.
const splitTerm = (text: string): { term?: string; rest: string } => {
  const at = text.indexOf(' — ');
  const term = at > 0 ? text.slice(0, at) : '';
  return term && term.length <= 40 && !term.includes(':') ? { term, rest: text.slice(at) } : { rest: text };
};
const pilotSections = (pilot?.sections ?? []).map(section => ({
  ...section,
  bullets: section.bullets?.map(splitTerm),
}));

// Quick draw (pilot pages): its position names are the worked example's, in draw order.
const quickDrawPositions = (pilot?.example.cards ?? []).map(card => card.position);

const exampleCards = (pilot?.example.cards ?? []).map(card => ({
  ...card,
  name: CARD_SEO_DATA[card.cardSlug]?.name ?? card.cardSlug,
  number: CARD_SEO_DATA[card.cardSlug]?.number ?? '',
}));

// «Все расклады» (pilot pages only): every spread page, so each test page links to all 16.
const allSpreads = Object.values(SPREAD_SEO_DATA).map(s => ({ slug: s.slug, title: s.title }));

const CHIP_TILTS = [-1.2, 0.8, -0.5, 1, -0.9, 0.6];

const cardWord = computed(() => {
  if (!spread) return '';
  const n = spread.cardCount;
  if (n === 1) return 'карта';
  if (n >= 2 && n <= 4) return 'карты';
  return 'карт';
});

if (spread) {
  useHead({
    title: metaTitle,
    meta: [
      { name: 'description', content: metaDescription },
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: metaTitle },
      { property: 'og:description', content: metaDescription },
      { property: 'og:image', content: 'https://tarotmeow.ru/og-image.jpg' },
      { property: 'og:locale', content: 'ru_RU' },
      { property: 'og:site_name', content: 'Таро Мяу' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: metaTitle },
      { name: 'twitter:description', content: metaDescription },
      { name: 'twitter:image', content: 'https://tarotmeow.ru/og-image.jpg' },
    ],
    link: [
      { rel: 'canonical', href: `https://tarotmeow.ru/rasklady/${spread.slug}` },
      { rel: 'alternate', hreflang: 'ru', href: `https://tarotmeow.ru/rasklady/${spread.slug}` },
      { rel: 'alternate', hreflang: 'x-default', href: `https://tarotmeow.ru/rasklady/${spread.slug}` },
    ],
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Главная', item: 'https://tarotmeow.ru/' },
            { '@type': 'ListItem', position: 2, name: 'Расклады', item: 'https://tarotmeow.ru/rasklady' },
            { '@type': 'ListItem', position: 3, name: spread.title, item: `https://tarotmeow.ru/rasklady/${spread.slug}` },
          ],
        }),
      },
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqItems.map(item => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        }),
      },
    ],
  });
}
</script>

<template>
  <article
    v-if="spread"
    class="pb-4"
  >
    <!-- ============================ HERO ============================ -->
    <!-- The spread's own patch (the same fabric as its tile in the app), her drawing
         tucked into its bottom edge — or, for spreads without one, a fan of face-down
         cards, one per card in the reading. -->
    <section class="pt-6 pb-14 md:pt-10 md:pb-20">
      <div class="site-container">
        <SeoCrumbs :items="[{ label: 'Главная', to: '/' }, { label: 'Расклады', to: '/rasklady' }, { label: spread.title }]" />

        <div
          class="paper-sheet relative flex flex-col overflow-hidden px-7 pt-9 text-foreground md:flex-row md:items-end md:gap-10 md:px-12 md:pt-12"
          :class="[spread.stock, spread.fabric]"
        >
          <div class="relative z-[1] flex-1 space-y-5 md:pb-12">
            <div class="flex flex-wrap items-center gap-2.5">
              <span
                class="seo-tag paper-tag type-overline px-2.5 py-0.5"
                style="--tilt: -1.5deg"
              >
                <Sparkles
                  v-if="spread.free"
                  class="h-3 w-3"
                  aria-hidden="true"
                />
                <Lock
                  v-else
                  class="h-3 w-3"
                  aria-hidden="true"
                />
                {{ spread.free ? 'Бесплатно' : 'Подписка' }}
              </span>
              <span
                class="seo-tag paper-tag type-overline px-2.5 py-0.5"
                style="--tilt: 1deg"
              >
                {{ spread.cardCount }} {{ cardWord }} · персональная интерпретация
              </span>
            </div>

            <h1 class="type-display">{{ heading }}</h1>

            <div class="flex flex-col gap-3 pt-1 sm:flex-row sm:flex-wrap">
              <Button
                as-child
                size="lg"
              >
                <RouterLink :to="spread.readingRoute">Попробовать расклад</RouterLink>
              </Button>
              <Button
                as-child
                variant="outline"
                size="lg"
              >
                <a
                  :href="TELEGRAM_BOT_URL"
                  target="_blank"
                  rel="noopener"
                >{{ TELEGRAM_CTA_LABEL }}</a>
              </Button>
            </div>
          </div>

          <div
            v-if="art"
            class="relative z-[1] mt-8 w-[220px] shrink-0 self-end md:mt-0 md:w-[280px]"
            :class="art.tuck === 'bottom-right' ? '-mr-7 md:-mr-12' : art.tuck ? 'self-center md:self-end' : 'mb-7 self-center md:mb-10 md:self-end'"
            aria-hidden="true"
          >
            <!-- Cut edges must run past the patch's edges (it clips): bottom-right art overshoots
                 both ways, bottom-tucked art downwards (as SpreadCard / MegaSpreads). -->
            <div :class="art.tuck === 'bottom-right' ? 'translate-x-[7%] translate-y-[7%]' : art.tuck && 'translate-y-[4%]'">
              <div
                class="mx-auto"
                :style="{ width: `${art.width}%` }"
              >
                <PaperCutout
                  :name="art.name"
                  :still="art.still"
                />
              </div>
            </div>
          </div>
          <div
            v-else
            class="relative z-[1] mt-8 self-end md:mt-0 md:self-end"
            aria-hidden="true"
          >
            <div class="translate-y-[16%]">
              <CardFan
                :faces="fanBacks"
                :card-width="72"
                :spread="11"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ======================== QUICK DRAW ======================== -->
    <!-- Pilot pages: draw the spread's cards right here (face-down in the HTML, drawn on
         click), the personal reading in the app is the next step. -->
    <SpreadQuickDraw
      v-if="spread.quickDraw"
      :spread-slug="spread.slug"
      :card-count="spread.cardCount"
      :reading-route="spread.readingRoute"
      :positions="quickDrawPositions"
    />

    <!-- ========================= SEO PILOT ========================= -->
    <!-- The 4 pilot pages: reviewed long-form copy (intro, topical sections with internal
         links, a worked example, FAQ, every spread) in place of the generic blocks below.
         v-if / v-else so the control pages render byte-for-byte as before; the v-else below
         is the original INTRO. (Keep comments out from between the two branches — in dev a
         comment there turns the else branch into a fragment.) -->
    <div v-if="pilot">
      <section class="pb-14 md:pb-20">
        <div class="site-container">
          <ScrollReveal class="mx-auto max-w-[760px] space-y-5">
            <p
              v-for="(paragraph, i) in pilotIntro"
              :key="i"
              class="type-body text-muted-foreground md:text-lg md:leading-[1.7]"
            >
              {{ paragraph }}
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section
        v-for="section in pilotSections"
        :key="section.h2"
        class="pb-14 md:pb-20"
      >
        <div class="site-container">
          <div class="mx-auto max-w-[760px]">
            <ScrollReveal>
              <h2 class="type-title mb-5">{{ section.h2 }}</h2>
              <div class="space-y-4">
                <p
                  v-for="(paragraph, i) in section.paragraphs"
                  :key="i"
                  class="type-body text-muted-foreground md:text-[17px] md:leading-[1.7]"
                >
                  {{ paragraph }}
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal
              v-if="section.bullets?.length"
              as="ul"
              class="seo-lines paper-sheet mt-6 px-5 md:px-6"
            >
              <li
                v-for="(bullet, i) in section.bullets"
                :key="i"
                class="flex gap-3.5 py-3.5"
              >
                <span
                  class="seo-disc mt-[0.6em] h-2 w-2"
                  aria-hidden="true"
                />
                <span class="type-body text-muted-foreground">
                  <strong
                    v-if="bullet.term"
                    class="type-body-strong text-foreground"
                  >{{ bullet.term }}</strong>{{ bullet.rest }}
                </span>
              </li>
            </ScrollReveal>

            <nav
              v-if="section.links?.length"
              :aria-label="`По теме: ${section.h2}`"
              class="mt-6 flex flex-wrap gap-x-2.5 gap-y-3"
            >
              <RouterLink
                v-for="(link, i) in section.links"
                :key="link.to"
                :to="link.to"
                class="seo-tag seo-press paper-tag type-label px-3 py-1"
                :style="{ '--tilt': `${CHIP_TILTS[i % CHIP_TILTS.length]}deg` }"
              >
                {{ link.label }}
              </RouterLink>
            </nav>
          </div>
        </div>
      </section>

      <SeoSpreadExample
        :question="pilot.example.question"
        :cards="exampleCards"
        :summary="pilot.example.summary"
      />

      <!-- FAQ and the catalog sit in the same centred column as the pilot copy above. -->
      <section class="pb-14 md:pb-20">
        <div class="site-container">
          <div class="mx-auto max-w-[760px]">
          <ScrollReveal class="mb-8">
            <div class="type-overline mb-3.5 text-primary-ink">
              Вопросы и ответы
            </div>
            <h2 class="type-title">Частые вопросы</h2>
          </ScrollReveal>

          <div class="space-y-3.5">
            <ScrollReveal
              v-for="(faq, i) in faqItems"
              :key="i"
              :delay="((i % 3) + 1) * 80"
            >
              <SeoFaqItem
                :question="faq.question"
                :answer="faq.answer"
              />
            </ScrollReveal>
          </div>
          </div>
        </div>
      </section>

      <section class="pb-14 md:pb-20">
        <div class="site-container">
          <div class="mx-auto max-w-[760px]">
          <ScrollReveal class="mb-8">
            <div class="type-overline mb-3.5 text-primary-ink">
              Каталог
            </div>
            <h2 class="type-title">Все расклады</h2>
          </ScrollReveal>

          <nav
            aria-label="Все расклады"
            class="flex flex-wrap gap-x-2.5 gap-y-3"
          >
            <template
              v-for="(item, i) in allSpreads"
              :key="item.slug"
            >
              <span
                v-if="item.slug === spread.slug"
                aria-current="page"
                class="type-label px-3 py-1 text-foreground"
              >{{ item.title }}</span>
              <RouterLink
                v-else
                :to="`/rasklady/${item.slug}`"
                class="seo-tag seo-press paper-tag type-label px-3 py-1"
                :style="{ '--tilt': `${CHIP_TILTS[i % CHIP_TILTS.length]}deg` }"
              >
                {{ item.title }}
              </RouterLink>
            </template>
          </nav>
          </div>
        </div>
      </section>
    </div>
    <section
      v-else
      class="pb-14 md:pb-20"
    >
      <div class="site-container">
        <ScrollReveal class="mx-auto max-w-[760px]">
          <p class="type-body text-muted-foreground md:text-lg md:leading-[1.7]">{{ spread.content.intro }}</p>
          <p
            v-if="spread.guide"
            class="type-caption mt-5"
          >
            Не знаешь, как сформулировать?
            <RouterLink
              :to="spread.guide.to"
              class="seo-link font-semibold"
            >{{ spread.guide.label }}</RouterLink>
            — принципы и примеры.
          </p>
        </ScrollReveal>
      </div>
    </section>

    <!-- ====================== КАК ЭТО РАБОТАЕТ ====================== -->
    <section
      v-if="!pilot"
      class="pb-14 md:pb-20"
    >
      <div class="site-container">
        <ScrollReveal class="mb-8">
          <div class="type-overline mb-3.5 text-primary-ink">
            Шаг за шагом
          </div>
          <h2 class="type-title">Как это работает</h2>
        </ScrollReveal>

        <div class="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
          <ScrollReveal
            v-for="(step, i) in spread.content.howItWorks"
            :key="i"
            :delay="((i % 2) + 1) * 80"
            class="sd-step flex gap-4 pt-5"
          >
            <div class="seo-disc type-app-heading h-10 w-10">
              {{ String(i + 1).padStart(2, '0') }}
            </div>
            <p class="type-body text-muted-foreground">{{ step }}</p>
          </ScrollReveal>
        </div>
      </div>
    </section>

    <!-- ======================== ЧТО ТЫ УЗНАЕШЬ ====================== -->
    <section
      v-if="!pilot"
      class="pb-14 md:pb-20"
    >
      <div class="site-container">
        <ScrollReveal class="mb-8">
          <div class="type-overline mb-3.5 text-primary-ink">
            Результат
          </div>
          <h2 class="type-title">Что ты узнаешь</h2>
        </ScrollReveal>

        <!-- Columns by count, so no card is left alone on a row: 4 → 2×2 on desktop; an odd last one spans the row on tablets. -->
        <div
          class="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:[&>*:last-child:nth-child(odd)]:col-span-2"
          :class="spread.content.whatYouLearn.length % 3 === 1 ? '' : 'lg:grid-cols-3 lg:[&>*:last-child:nth-child(odd)]:col-span-1'"
        >
          <ScrollReveal
            v-for="(item, i) in spread.content.whatYouLearn"
            :key="i"
            :delay="((i % 3) + 1) * 80"
          >
            <div class="paper-sheet flex h-full items-start gap-3.5 p-5 md:p-6">
              <span
                class="seo-disc mt-0.5 h-7 w-7"
                aria-hidden="true"
              >
                <Check
                  class="h-3.5 w-3.5"
                  :stroke-width="3"
                />
              </span>
              <span class="type-body text-muted-foreground">{{ item }}</span>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>

    <!-- ======================== КОМУ ПОДОЙДЁТ ====================== -->
    <!-- A cream note. -->
    <section
      v-if="!pilot"
      class="pb-14 md:pb-20"
    >
      <div class="site-container">
        <ScrollReveal class="mx-auto max-w-[860px]">
          <div
            class="seo-slip paper-tag p-7 text-paper-stock-ink md:p-10"
            style="--tilt: -0.4deg"
          >
            <div class="type-overline mb-3.5">
              Для кого
            </div>
            <h2 class="type-title mb-4">Кому подойдёт</h2>
            <p class="type-body max-w-[720px] md:text-[17px]">{{ spread.content.whoIsItFor }}</p>
          </div>
        </ScrollReveal>
      </div>
    </section>

    <!-- =========================== FAQ ============================ -->
    <section
      v-if="!pilot"
      class="pb-14 md:pb-20"
    >
      <div class="site-container">
        <ScrollReveal class="mb-8">
          <div class="type-overline mb-3.5 text-primary-ink">
            Вопросы и ответы
          </div>
          <h2 class="type-title">Частые вопросы</h2>
        </ScrollReveal>

        <div class="max-w-[820px] space-y-3.5">
          <ScrollReveal
            v-for="(faq, i) in spread.content.faq"
            :key="i"
            :delay="((i % 3) + 1) * 80"
          >
            <SeoFaqItem
              :question="faq.question"
              :answer="faq.answer"
            />
          </ScrollReveal>
        </div>
      </div>
    </section>

    <!-- ====================== ПОХОЖИЕ РАСКЛАДЫ ===================== -->
    <section
      v-if="relatedSpreads.length"
      class="pb-14 md:pb-20"
    >
      <div class="site-container">
        <ScrollReveal class="mb-8 flex items-end justify-between gap-4">
          <div>
            <div class="type-overline mb-3.5 text-primary-ink">
              Не останавливайся
            </div>
            <h2 class="type-title">Похожие расклады</h2>
          </div>
          <RouterLink
            to="/rasklady"
            class="seo-more type-label hidden shrink-0 sm:block"
          >
            Все расклады →
          </RouterLink>
        </ScrollReveal>

        <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <SeoSpreadPatch
            v-for="(related, i) in relatedSpreads"
            :key="related.slug"
            :to="`/rasklady/${related.slug}`"
            :stock="related.stock"
            :fabric="related.fabric"
            :fan="Math.min(related.cardCount, 3)"
            :fan-width="26"
            class="min-h-[168px] p-4 md:p-5"
          >
            <span
              class="seo-tag paper-tag type-overline w-fit px-2 py-0.5"
              :style="{ '--tilt': `${i % 2 ? 1.5 : -1.5}deg` }"
            >
              <Sparkles
                v-if="related.free"
                class="h-2.5 w-2.5"
                aria-hidden="true"
              />
              <Lock
                v-else
                class="h-2.5 w-2.5"
                aria-hidden="true"
              />
              {{ related.free ? 'Бесплатно' : 'Подписка' }}
            </span>
            <div class="mt-auto pt-4">
              <h3 class="type-app-heading">{{ related.title }}</h3>
              <span class="type-label mt-1 inline-block">Открыть →</span>
            </div>
          </SeoSpreadPatch>
        </div>
      </div>
    </section>

    <!-- ========================= BOTTOM CTA ======================== -->
    <div class="site-container py-6 md:py-10">
      <ScrollReveal>
        <SeoFinalCta>
          <h2 class="type-display">
            Готова <span class="text-primary-ink">попробовать</span>?
          </h2>
          <p class="type-body mx-auto mb-8 mt-[18px] max-w-[520px] text-muted-foreground md:text-lg">
            Первый расклад бесплатно — спроси кота прямо сейчас.
          </p>
          <div class="flex flex-col items-center justify-center gap-3 sm:flex-row sm:flex-wrap">
            <Button
              as-child
              size="lg"
            >
              <RouterLink :to="APP_CTA_TO">{{ APP_CTA_LABEL }}</RouterLink>
            </Button>
            <Button
              as-child
              variant="outline"
              size="lg"
            >
              <a
                :href="TELEGRAM_BOT_URL"
                target="_blank"
                rel="noopener"
              >{{ TELEGRAM_CTA_LABEL }}</a>
            </Button>
            <Button
              as-child
              variant="ghost"
              size="lg"
            >
              <RouterLink to="/rasklady">Все расклады</RouterLink>
            </Button>
          </div>
        </SeoFinalCta>
      </ScrollReveal>
    </div>
  </article>
</template>

<style scoped src="../components/seo/seo-paper.css"></style>

<style scoped>
.sd-step {
  border-top: 1px dashed rgb(242 235 221 / 0.14);
}
</style>
